import React, { useState, useCallback, useRef, useEffect, useMemo } from "react";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import { useAuth0 } from "@auth0/auth0-react";
import { SubHeader } from "layout/SubHeader";
import Button from "components/button/Button";
import { DateRangePicker } from "rsuite";
import "rsuite/dist/rsuite.min.css";
import AuthService from "Lib/API/Auth";
import { getDateFormatForDatePicket } from "helper/dateFormat";
import {
  WIDGET_REGISTRY,
  loadEnabledWidgets,
  saveEnabledWidgets,
  loadWidgetSettings,
  saveWidgetSettings,
  getWidgetBaseId,
  createInstanceId,
  getRegistryEntry,
  isWidgetPermitted,
} from "./DashboardConfig";
import OutstandingWidgetConfig from "./widgets/OutstandingWidgetConfig";
import RevenueWidgetConfig from "./widgets/RevenueWidgetConfig";
import RecentTransactionsWidgetConfig from "./widgets/RecentTransactionsWidgetConfig";
import FiscalYearConfig from "./FiscalYearConfig";
import WidgetPicker from "./WidgetPicker";
import RevenueWidget from "./widgets/RevenueWidget";
import OutstandingWidget from "./widgets/OutstandingWidget";
import DonationBoxWidget from "./widgets/DonationBoxWidget";
import MemberCountWidget from "./widgets/MemberCountWidget";
import YahrzeitWidget from "./widgets/YahrzeitWidget";
import EventsWidget from "./widgets/EventsWidget";
import RecentTransactionsWidget from "./widgets/RecentTransactionsWidget";
import SchoolEnrollmentWidget from "./widgets/SchoolEnrollmentWidget";
import BirthdaysWidget from "./widgets/BirthdaysWidget";
import AnniversariesWidget from "./widgets/AnniversariesWidget";
import DonationsWidget from "./widgets/DonationsWidget";
import FuturePaymentsWidget from "./widgets/FuturePaymentsWidget";
import "./dashboard.scss";

const WIDGET_COMPONENTS = {
  revenue: RevenueWidget,
  outstanding: OutstandingWidget,
  donationBox: DonationBoxWidget,
  memberCount: MemberCountWidget,
  yahrzeits: YahrzeitWidget,
  events: EventsWidget,
  recentTransactions: RecentTransactionsWidget,
  schoolEnrollment: SchoolEnrollmentWidget,
  birthdays: BirthdaysWidget,
  anniversaries: AnniversariesWidget,
  donations: DonationsWidget,
  futurePayments: FuturePaymentsWidget,
};

const DATE_RANGE_WIDGETS = new Set(["revenue", "donationBox", "recentTransactions", "donations", "outstanding", "memberCount"]);

const now = new Date();
const y = now.getFullYear();
const m = now.getMonth();

const eod = (d) => { const r = new Date(d); r.setHours(23, 59, 59, 999); return r; };

const DEFAULT_RANGE = [new Date(y, 0, 1), eod(new Date(y, 11, 31))];

const FISCAL_KEY = "dashboard_fiscal_year";
const RANGE_LABEL_KEY = "dashboard_range_label";

const loadFiscal = () => {
  try { return JSON.parse(localStorage.getItem(FISCAL_KEY)) || { startMonth: 1, startDay: 1, endMonth: 12, endDay: 31 }; }
  catch { return { startMonth: 1, startDay: 1, endMonth: 12, endDay: 31 }; }
};

const computePresets = ({ startMonth, startDay, endMonth, endDay }) => {
  const today = new Date();
  const fyStartThisYear = new Date(today.getFullYear(), startMonth - 1, startDay);
  const fyStartYear = today >= fyStartThisYear ? today.getFullYear() : today.getFullYear() - 1;
  const fyStart = new Date(fyStartYear, startMonth - 1, startDay);
  const endIsNextYear = endMonth < startMonth || (endMonth === startMonth && endDay < startDay);
  const fyEndYear = endIsNextYear ? fyStartYear + 1 : fyStartYear;
  const fyEnd = new Date(fyEndYear, endMonth - 1, endDay);
  const lastFyStart = new Date(fyStartYear - 1, startMonth - 1, startDay);
  const lastFyEnd = new Date(fyEndYear - 1, endMonth - 1, endDay);
  return [
    { label: "Today",            value: [new Date(y, m, now.getDate()), eod(new Date(y, m, now.getDate()))] },
    { label: "Yesterday",        value: [new Date(y, m, now.getDate() - 1), eod(new Date(y, m, now.getDate() - 1))] },
    { label: "Last 7 days",      value: [new Date(now - 7 * 86400000), eod(now)] },
    { label: "Last 30 days",     value: [new Date(now - 30 * 86400000), eod(now)] },
    { label: "This month",       value: [new Date(y, m, 1), eod(new Date(y, m + 1, 0))] },
    { label: "Last month",       value: [new Date(y, m - 1, 1), eod(new Date(y, m, 0))] },
    { label: "This year",        value: [new Date(y, 0, 1), eod(new Date(y, 11, 31))] },
    { label: "This fiscal year", value: [fyStart, eod(fyEnd)] },
    { label: "Last fiscal year", value: [lastFyStart, eod(lastFyEnd)] },
  ];
};

const loadInitialRange = () => {
  const label = localStorage.getItem(RANGE_LABEL_KEY);
  if (label) {
    const preset = computePresets(loadFiscal()).find((p) => p.label === label);
    if (preset) return preset.value;
  }
  return DEFAULT_RANGE;
};

const Dashboard = () => {
  const { getAccessTokenSilently } = useAuth0();
  const [enabledIds, setEnabledIds] = useState(() => loadEnabledWidgets());
  const [widgetSettings, setWidgetSettings] = useState(() => loadWidgetSettings());
  const [customizeMode, setCustomizeMode] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [rangeValue, setRangeValue] = useState(loadInitialRange);

  const [fiscalConfig, setFiscalConfig] = useState(loadFiscal);
  const [fyOpen, setFyOpen] = useState(false);

  const PREDEFINED_RANGES = useMemo(() => computePresets(fiscalConfig), [fiscalConfig]);

  const PRIOR_PRESET_MAP = { "Today": "Yesterday", "This month": "Last month", "This fiscal year": "Last fiscal year" };

  const dateRange = useMemo(() => {
    if (!rangeValue) return null;
    const match = PREDEFINED_RANGES.find((p) =>
      Math.abs(p.value[0] - rangeValue[0]) < 1000 && Math.abs(p.value[1] - rangeValue[1]) < 1000
    );
    const priorLabel = match ? PRIOR_PRESET_MAP[match.label] : null;
    const priorPreset = priorLabel ? PREDEFINED_RANGES.find((p) => p.label === priorLabel) : null;
    return {
      from: rangeValue[0],
      to: rangeValue[1],
      ...(priorPreset ? { priorFrom: priorPreset.value[0], priorTo: priorPreset.value[1] } : {}),
    };
  }, [rangeValue, PREDEFINED_RANGES]); // eslint-disable-line react-hooks/exhaustive-deps

  const dragSrcId = useRef(null);
  const [dragOverId, setDragOverId] = useState(null);

  // Load settings from DB on mount; fall back to localStorage if unavailable
  useEffect(() => {
    let cancelled = false;
    getAccessTokenSilently().then((token) =>
      AuthService.getDashboardSettings(token)
    ).then((data) => {
      if (cancelled) return;
      if (data?.enabledWidgets?.length) {
        setEnabledIds((prev) =>
          JSON.stringify(prev) === JSON.stringify(data.enabledWidgets) ? prev : data.enabledWidgets
        );
        saveEnabledWidgets(data.enabledWidgets);
      }
      if (data?.widgetSettings && Object.keys(data.widgetSettings).length) {
        setWidgetSettings((prev) =>
          JSON.stringify(prev) === JSON.stringify(data.widgetSettings) ? prev : data.widgetSettings
        );
        saveWidgetSettings(data.widgetSettings);
      }
      if (data?.fiscalConfig) {
        setFiscalConfig((prev) =>
          JSON.stringify(prev) === JSON.stringify(data.fiscalConfig) ? prev : data.fiscalConfig
        );
        localStorage.setItem(FISCAL_KEY, JSON.stringify(data.fiscalConfig));
      }
      if (data?.rangeLabel) {
        const fiscal = data.fiscalConfig || loadFiscal();
        const preset = computePresets(fiscal).find((p) => p.label === data.rangeLabel);
        if (preset) {
          setRangeValue((prev) =>
            prev?.[0]?.getTime?.() === preset.value[0]?.getTime?.() &&
            prev?.[1]?.getTime?.() === preset.value[1]?.getTime?.()
              ? prev
              : preset.value
          );
        }
        localStorage.setItem(RANGE_LABEL_KEY, data.rangeLabel);
      }
    }).catch(() => {/* keep localStorage values */});
    return () => { cancelled = true; };
  }, [getAccessTokenSilently]);

  const handleSave = useCallback((ids, settings) => {
    setEnabledIds(ids);
    saveEnabledWidgets(ids);
    const nextSettings = settings || widgetSettings;
    setWidgetSettings(nextSettings);
    saveWidgetSettings(nextSettings);
    // Persist to DB (fire and forget)
    getAccessTokenSilently().then((token) =>
      AuthService.saveDashboardSettings(token, { enabledWidgets: ids, widgetSettings: nextSettings })
    ).catch(() => {});
    setPickerOpen(false);
  }, [getAccessTokenSilently, widgetSettings]);

  const handleRemove = useCallback((id) => {
    setEnabledIds((prev) => {
      const next = prev.filter((x) => x !== id);
      saveEnabledWidgets(next);
      getAccessTokenSilently().then((token) =>
        AuthService.saveDashboardSettings(token, { enabledWidgets: next, widgetSettings })
      ).catch(() => {});
      return next;
    });
  }, [getAccessTokenSilently, widgetSettings]);

  const handleDragStart = (e, id) => {
    dragSrcId.current = id;
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e, id) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (id !== dragSrcId.current) setDragOverId(id);
  };

  const handleDrop = (e, targetId) => {
    e.preventDefault();
    const srcId = dragSrcId.current;
    if (!srcId || srcId === targetId) return;
    setEnabledIds((prev) => {
      const arr = [...prev];
      const srcIdx = arr.indexOf(srcId);
      const tgtIdx = arr.indexOf(targetId);
      if (srcIdx === -1 || tgtIdx === -1) return prev;
      arr.splice(srcIdx, 1);
      arr.splice(tgtIdx, 0, srcId);
      saveEnabledWidgets(arr);
      getAccessTokenSilently().then((token) =>
        AuthService.saveDashboardSettings(token, { enabledWidgets: arr, widgetSettings })
      ).catch(() => {});
      return arr;
    });
    dragSrcId.current = null;
    setDragOverId(null);
  };

  const handleDragEnd = () => {
    dragSrcId.current = null;
    setDragOverId(null);
  };

  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);

  const [configureId, setConfigureId] = useState(null);

  const handleResetToDefault = useCallback(() => {
    const defaults = WIDGET_REGISTRY.filter((w) => w.defaultEnabled && isWidgetPermitted(w)).map((w) => w.id);
    setEnabledIds(defaults);
    saveEnabledWidgets(defaults);
    setWidgetSettings({});
    saveWidgetSettings({});
    setRangeValue(DEFAULT_RANGE);
    localStorage.removeItem(RANGE_LABEL_KEY);
    getAccessTokenSilently().then((token) =>
      AuthService.saveDashboardSettings(token, { enabledWidgets: defaults, widgetSettings: {}, rangeLabel: null })
    ).catch(() => {});
    setResetConfirmOpen(false);
  }, [getAccessTokenSilently]);

  const handleConfigureSave = useCallback((settings) => {
    if (!configureId) return;
    const isNew = !enabledIds.includes(configureId);
    const nextIds = isNew ? [...enabledIds, configureId] : enabledIds;
    const nextSettings = { ...widgetSettings, [configureId]: settings };
    setWidgetSettings(nextSettings);
    saveWidgetSettings(nextSettings);
    if (isNew) {
      setEnabledIds(nextIds);
      saveEnabledWidgets(nextIds);
    }
    getAccessTokenSilently().then((token) =>
      AuthService.saveDashboardSettings(token, { enabledWidgets: nextIds, widgetSettings: nextSettings })
    ).catch(() => {});
    setConfigureId(null);
  }, [configureId, enabledIds, widgetSettings, getAccessTokenSilently]);

  // Map instance IDs to their registry entry (supports multi-instance widgets)
  const enabledWidgets = enabledIds
    .map((id) => {
      const entry = getRegistryEntry(id);
      return entry ? { ...entry, id } : null;
    })
    .filter(Boolean)
    .filter((w) => isWidgetPermitted(w));

  return (
    <div className="dashboard-page pt-10 pb-8">
      {/* ── Header ── */}
      <div className="d-flex justify-content-between align-items-center mb-6">
        <SubHeader title="Dashboard" color="#2455EF" />
        <div className="d-flex align-items-center" style={{ gap: 8 }}>
          <DateRangePicker
            value={rangeValue}
            onChange={(val) => {
              const next = val ? [val[0], eod(val[1])] : DEFAULT_RANGE;
              setRangeValue(next);
              const match = PREDEFINED_RANGES.find((p) =>
                Math.abs(p.value[0] - next[0]) < 1000 && Math.abs(p.value[1] - next[1]) < 1000
              );
              const label = match?.label || null;
              if (label) localStorage.setItem(RANGE_LABEL_KEY, label);
              else localStorage.removeItem(RANGE_LABEL_KEY);
              getAccessTokenSilently().then((token) =>
                AuthService.saveDashboardSettings(token, {
                  enabledWidgets: enabledIds, widgetSettings, fiscalConfig, rangeLabel: label,
                })
              ).catch(() => {});
            }}
            ranges={PREDEFINED_RANGES}
            format={getDateFormatForDatePicket()}
            placeholder="Select date range"
            cleanable={false}
            placement="bottomEnd"
            size="md"
            style={{ width: 260 }}
          />
          {customizeMode && (
            <Button
              variant="primary-outline"
              size="sm"
              style={{ whiteSpace: "nowrap" }}
              onClick={() => setResetConfirmOpen(true)}
            >
              Reset to default
            </Button>
          )}
          {customizeMode && (
            <Button
              variant="primary-outline"
              size="sm"
              style={{ whiteSpace: "nowrap" }}
              onClick={() => setFyOpen(true)}
            >
              Fiscal Year
            </Button>
          )}
          {customizeMode && (
            <Button
              variant="primary-outline"
              size="sm"
              style={{ whiteSpace: "nowrap" }}
              onClick={() => setPickerOpen(true)}
            >
              Add Widgets
            </Button>
          )}
          <Button
            variant={customizeMode ? "primary" : "primary-outline"}
            size="sm"
            onClick={() => setCustomizeMode((v) => !v)}
          >
            {customizeMode ? "Done" : "Customize"}
          </Button>
        </div>
      </div>

      {/* ── Widget grid ── */}
      {enabledWidgets.length === 0 ? (
        <div className="dashboard-page__empty">
          <p>No widgets enabled.</p>
          <Button variant="primary" size="sm" onClick={() => setPickerOpen(true)}>
            Add Widgets
          </Button>
        </div>
      ) : (
        <div className="dashboard-grid">
          {enabledWidgets.map((w) => {
            const baseId = getWidgetBaseId(w.id);
            const Component = WIDGET_COMPONENTS[baseId];
            if (!Component) return null;
            const isDragging = dragSrcId.current === w.id;
            const isOver = dragOverId === w.id;
            const registryEntry = getRegistryEntry(w.id);
            return (
              <div
                key={w.id}
                className={[
                  "dashboard-grid__cell",
                  `dashboard-grid__cell--${w.cols}`,
                  w.rows ? `dashboard-grid__cell--rows-${w.rows}` : "",
                  customizeMode ? "dashboard-grid__cell--editable" : "",
                  isDragging ? "dashboard-grid__cell--dragging" : "",
                  isOver ? "dashboard-grid__cell--drag-over" : "",
                ].join(" ")}
                draggable={customizeMode}
                onDragStart={(e) => handleDragStart(e, w.id)}
                onDragOver={(e) => handleDragOver(e, w.id)}
                onDrop={(e) => handleDrop(e, w.id)}
                onDragEnd={handleDragEnd}
              >
                {customizeMode && (
                  <div className="drag-handle" title="Drag to reorder">
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor">
                      <rect x="2" y="2" width="10" height="2" rx="1"/>
                      <rect x="2" y="6" width="10" height="2" rx="1"/>
                      <rect x="2" y="10" width="10" height="2" rx="1"/>
                    </svg>
                  </div>
                )}
                <Component
                  customizeMode={customizeMode}
                  onRemove={() => handleRemove(w.id)}
                  onConfigure={registryEntry?.multiInstance ? () => setConfigureId(w.id) : undefined}
                  dateRange={DATE_RANGE_WIDGETS.has(baseId) ? dateRange : undefined}
                  settings={widgetSettings[w.id]}
                />
              </div>
            );
          })}
        </div>
      )}

      <WidgetPicker
        open={pickerOpen}
        enabled={enabledIds}
        settings={widgetSettings}
        onClose={() => setPickerOpen(false)}
        onSave={handleSave}
        onAddInstance={(baseId) => {
          setPickerOpen(false);
          setConfigureId(createInstanceId(baseId));
        }}
      />

      <Dialog open={resetConfirmOpen} onClose={() => setResetConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Reset to default</DialogTitle>
        <DialogContent>
          <p style={{ margin: 0, fontSize: 14, color: "#374151" }}>
            This will restore the default widgets and layout. Your current configuration will be lost.
          </p>
        </DialogContent>
        <DialogActions>
          <Button variant="primary-outline" size="sm" onClick={() => setResetConfirmOpen(false)}>
            Cancel
          </Button>
          <Button variant="primary" size="sm" onClick={handleResetToDefault}>
            Reset
          </Button>
        </DialogActions>
      </Dialog>

      <FiscalYearConfig
        open={fyOpen}
        onClose={() => setFyOpen(false)}
        onSave={(config) => {
          setFiscalConfig(config);
          localStorage.setItem(FISCAL_KEY, JSON.stringify(config));
          getAccessTokenSilently().then((token) =>
            AuthService.saveDashboardSettings(token, {
              enabledWidgets: enabledIds, widgetSettings,
              fiscalConfig: config,
              rangeLabel: localStorage.getItem(RANGE_LABEL_KEY) || null,
            })
          ).catch(() => {});
          setFyOpen(false);
        }}
        initialConfig={fiscalConfig}
      />
      <OutstandingWidgetConfig
        open={!!configureId && getWidgetBaseId(configureId) === "outstanding"}
        isNew={!!configureId && !enabledIds.includes(configureId)}
        onClose={() => setConfigureId(null)}
        onSave={handleConfigureSave}
        initialSettings={configureId ? widgetSettings[configureId] : undefined}
      />
      <RevenueWidgetConfig
        open={!!configureId && getWidgetBaseId(configureId) === "revenue"}
        isNew={!!configureId && !enabledIds.includes(configureId)}
        onClose={() => setConfigureId(null)}
        onSave={handleConfigureSave}
        initialSettings={configureId ? widgetSettings[configureId] : undefined}
      />
      <RecentTransactionsWidgetConfig
        open={!!configureId && getWidgetBaseId(configureId) === "recentTransactions"}
        isNew={!!configureId && !enabledIds.includes(configureId)}
        onClose={() => setConfigureId(null)}
        onSave={handleConfigureSave}
        initialSettings={configureId ? widgetSettings[configureId] : undefined}
      />
    </div>
  );
};

export default Dashboard;
